/**
 * Profile page shared primitives.
 *
 * These are the foundation building blocks for the whole profile page. The
 * admin sections AND the professional/corporate sections (built separately)
 * compose these so every card looks and behaves identically. Theme tokens
 * mirror the cream palette used across the New UI.
 *
 * Exports:
 *   <ProfileCard>          — section card shell (title, subtitle, header action)
 *   <Pill>                 — small status chip (tone: neutral|green|red|orange|teal|navy)
 *   <FieldRow>             — read-only label/value row
 *   <EditableFieldsCard>   — declarative card: read mode ↔ edit mode with Save/Cancel
 */

import { useState } from 'react';
import Button from '../common/Button';
import Icon from '../common/Icon';
import { Select } from '../common/FormControls';

const BORDER = '#ddd6c6';
const PANEL = '#f8f5ef';
const TEXT = '#08111f';
const MUTED = '#7a7060';

const PILL_TONES = {
  neutral: { background: '#efeada', color: '#5b5340' },
  green: { background: '#e8efe5', color: '#4a6b3f' },
  red: { background: '#f2e9e6', color: '#9a564a' },
  orange: { background: '#f3ebd7', color: '#8a6a1f' },
  teal: { background: '#e3f0ed', color: '#145f5f' },
  navy: { background: '#edeef3', color: '#3a4a66' },
  // legacy alias so existing <Pill tone="purple"> callers get navy instead of breaking
  purple: { background: '#edeef3', color: '#3a4a66' },
  gold: { background: '#f3ebd7', color: '#836016' },
};

export function Pill({ tone = 'neutral', children }) {
  const style = PILL_TONES[tone] || PILL_TONES.neutral;
  return (
    <span
      className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-[2px] rounded-full whitespace-nowrap"
      style={style}
    >
      {children}
    </span>
  );
}

export function ProfileCard({ title, subtitle, icon, action, children }) {
  return (
    <section
      className="rounded-xl border bg-white overflow-hidden"
      style={{ borderColor: BORDER, boxShadow: '0 4px 18px rgba(8,17,31,0.06)' }}
    >
      <div
        className="flex items-start gap-3 px-6 py-[18px] border-b"
        style={{ borderColor: '#eee6d8', background: PANEL }}
      >
        {icon && (
          <span className="leading-none mt-[2px] shrink-0" style={{ color: '#145f5f' }}>
            <Icon name={icon} size={19} />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <h3 className="m-0 text-[17px] font-bold" style={{ color: TEXT, fontFamily: "'Cormorant Garamond', serif", letterSpacing: '.2px' }}>
            {title}
          </h3>
          {subtitle && <p className="m-0 mt-[3px] text-[12px]" style={{ color: MUTED }}>{subtitle}</p>}
        </div>
        {action && <div className="flex-shrink-0">{action}</div>}
      </div>
      <div className="p-6">{children}</div>
    </section>
  );
}

export function FieldRow({ label, value, mono }) {
  const empty = value === null || value === undefined || value === '';
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-[1px] font-semibold mb-[3px]" style={{ color: MUTED }}>
        {label}
      </div>
      <div
        className={`text-[13px] ${mono ? 'font-mono' : ''}`}
        style={{ color: empty ? '#b3a892' : TEXT }}
      >
        {empty ? 'Not set' : value}
      </div>
    </div>
  );
}

function GhostButton({ onClick, children, disabled }) {
  return (
    <Button variant="outline" size="sm" onClick={onClick} disabled={disabled}>
      {children}
    </Button>
  );
}

function PrimaryButton({ onClick, children, disabled, type = 'button', loading = false }) {
  return (
    <Button variant="primary" size="sm" type={type} onClick={onClick} disabled={disabled} loading={loading}>
      {children}
    </Button>
  );
}

function FieldInput({ field, value, onChange }) {
  if (field.type === 'custom') {
    return field.renderEdit(value, onChange);
  }
  if (field.type === 'select') {
    return (
      <Select
        size="sm"
        value={value ?? ''}
        onChange={onChange}
        placeholder="Select…"
        options={field.options || []}
      />
    );
  }
  if (field.type === 'checkbox') {
    return (
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={value || false}
          onChange={(e) => onChange(e.target.checked)}
          className="w-4 h-4 rounded border border-gray-300 text-[#145f5f] focus:ring-[#145f5f]"
          style={{ accentColor: '#145f5f' }}
        />
        <label className="text-[11.5px]" style={{ color: MUTED }}>
          {field.helper}
        </label>
      </div>
    );
  }
  return (
    <input
      type="text"
      value={value ?? ''}
      placeholder={field.placeholder || ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 rounded-lg border text-[13px] bg-white outline-none focus:border-[#145f5f]"
      style={{ borderColor: BORDER, color: TEXT }}
    />
  );
}

/**
 * Declarative editable card. Renders `fields` in read mode; on "Edit" flips to
 * inputs with Save/Cancel. On save it calls `onSave(draft)` (only the editable
 * fields). `onSave` may be async and may throw — the card surfaces the error
 * inline and stays in edit mode so nothing is lost.
 *
 * field: {
 *   name, label,
 *   type?: 'text' | 'select' | 'checkbox' | 'custom'   (default 'text')
 *   options?: [{ value, label }]                        (for 'select')
 *   helper?: string                                     (for 'checkbox')
 *   placeholder?, mono?, required?,
 *   readValue?: (values) => node                      (override read display)
 *   renderEdit?: (value, onChange) => node             (for 'custom')
 *   editable?: boolean                                 (default true; false = always read-only)
 * }
 */
export function EditableFieldsCard({ title, subtitle, icon, fields, values, onSave, editable = true }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const startEdit = () => {
    const initial = {};
    fields.forEach((f) => {
      // Custom fields manage their own shape (e.g. a {day,month,year} object) —
      // don't coerce a missing value to '' or their editor gets a bad type.
      initial[f.name] = f.type === 'custom' ? values?.[f.name] : (values?.[f.name] ?? '');
    });
    setDraft(initial);
    setError(null);
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setError(null);
  };

  const setField = (name, value) => setDraft((prev) => ({ ...prev, [name]: value }));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      // Only send the fields this card owns and that are actually editable.
      const payload = {};
      fields.forEach((f) => {
        if (f.editable !== false) payload[f.name] = draft[f.name];
      });
      await onSave(payload);
      setEditing(false);
    } catch (err) {
      setError(err?.message || 'Could not save changes.');
    } finally {
      setSaving(false);
    }
  };

  const action = editable ? (
    editing ? (
      <div className="flex items-center gap-2">
        <GhostButton onClick={cancel} disabled={saving}>Cancel</GhostButton>
        <PrimaryButton onClick={save} loading={saving}>{saving ? 'Saving…' : 'Save'}</PrimaryButton>
      </div>
    ) : (
      <GhostButton onClick={startEdit}>Edit</GhostButton>
    )
  ) : null;

  return (
    <ProfileCard title={title} subtitle={subtitle} icon={icon} action={action}>
      {error && (
        <div className="mb-4 px-3 py-2 rounded-lg text-[12px]" style={{ background: '#f2e9e6', color: '#9a564a' }}>
          {error}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
        {fields.map((field) => (
          <div key={field.name}>
            {editing && field.editable !== false ? (
              <>
                <div className="text-[10.5px] uppercase tracking-[1px] font-semibold mb-[5px]" style={{ color: MUTED }}>
                  {field.label}
                </div>
                <FieldInput field={field} value={draft[field.name]} onChange={(v) => setField(field.name, v)} />
              </>
            ) : (
              field.type === 'checkbox' ? (
                <div>
                  <div className="text-[10.5px] uppercase tracking-[1px] font-semibold mb-[3px]" style={{ color: MUTED }}>
                    {field.label}
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={values?.[field.name] || false}
                      disabled
                      className="w-4 h-4 rounded border border-gray-300"
                      style={{ accentColor: '#145f5f' }}
                    />
                    <span className="text-[13px]" style={{ color: TEXT }}>
                      {values?.[field.name] ? 'Yes' : 'No'}
                    </span>
                  </div>
                  {field.helper && (
                    <div className="text-[10.5px] mt-1" style={{ color: MUTED }}>
                      {field.helper}
                    </div>
                  )}
                </div>
              ) : (
                <FieldRow
                  label={field.label}
                  value={field.readValue ? field.readValue(values) : values?.[field.name]}
                  mono={field.mono}
                />
              )
            )}
          </div>
        ))}
      </div>
    </ProfileCard>
  );
}

export { GhostButton, PrimaryButton };
